export interface DeferredPromise<T> {
  promise: Promise<T>;
  /* eslint-disable @typescript-eslint/no-explicit-any */
  resolve: (arg?: any) => void;
  reject: (arg?: any) => void;
  /* eslint-enable @typescript-eslint/no-explicit-any */
}

/**
 * Replicate `$q.defer()` functionality without any dependencies.
 * Source: https://stackoverflow.com/a/49825137/5770734
 */
export function createDeferredPromise<T>(): DeferredPromise<T> {
  /* eslint-disable @typescript-eslint/no-explicit-any */
  let resolve: (arg?: any) => void = () => undefined;
  let reject: (arg?: any) => void = () => undefined;
  /* eslint-enable @typescript-eslint/no-explicit-any */
  const promise = new Promise<T>((res, rej) => {
    resolve = res;
    reject = rej;
  });
  return { promise, reject, resolve };
}

// https://stackoverflow.com/questions/35834563/object-literal-hash-with-promise-all
export function promiseAllHash<T>(promises: { [K in keyof T]: Promise<T[K]> }): Promise<{
  [K in keyof T]: T[K];
}> {
  // Grab all the key values in order
  const promiseKeys = Object.keys(promises) as (keyof T)[];
  // Convert hash to Array
  const promisesMappedToArray = promiseKeys.map((key) => promises[key]);

  const addCurrentToResult = (
    result: {
      [K in keyof T]: T[K];
    },
    currentResolved: any,
    index: number
  ) => {
    // Look up the key based on index
    const matchingKey = promiseKeys[index];
    // Extend the result with the current value
    return { ...result, [matchingKey]: currentResolved };
  };

  const convertBackToHash = (resolved: any[]) => {
    return resolved.reduce(addCurrentToResult, {});
  };

  return Promise.all(promisesMappedToArray).then(convertBackToHash);
}

export function promiseAllArrayOrHash<TAll>(promises: Array<Promise<TAll>>): Promise<TAll[]>;
export function promiseAllArrayOrHash<T>(promises: { [K in keyof T]: Promise<T[K]> }): Promise<{
  [K in keyof T]: T[K];
}>;
export function promiseAllArrayOrHash<T>(promises: Array<Promise<T>> | { [K in keyof T]: Promise<T[K]> }) {
  if (Array.isArray(promises)) {
    return Promise.all(promises);
  }
  return promiseAllHash<T>(promises);
}
